import { useEffect, useMemo, useState } from 'react'
import { Search, Loader2, ChevronDown, ChevronUp, FileSearch, GitBranch, AlertCircle } from 'lucide-react'
import { dealsApi, scopesApi, cascadeApi } from '@api/client'
import type { Deal } from '@api/types'
import { mapDbScopeToStoreState } from '@features/scope/utils/mappers'
import { buildExportRows } from '@features/scope/utils/exporters'
import type { ScopeData } from '@features/scope/types'
import { inputCls } from '../styles'
import { InteractionDiagram } from './InteractionDiagram'
import { getInteractionDiagram } from '../interactionDiagrams'
import type { InteractionDiagramStage } from '../interactionDiagrams'

const SECTION_ORDER = [
  'General', 'KYC', 'AML', 'KYB', 'ID Verification', 'Biometrics',
  'Recipes/Workflows', 'Custom Work', 'Advanced', 'Rules', 'Risk', 'SOW',
]

function isMeaningful(value: string): boolean {
  const v = value.trim()
  return v.length > 0 && v !== '—' && v.toLowerCase() !== 'n/a' && v.toLowerCase() !== 'not set'
}

const PRODUCT_LABELS: Record<InteractionDiagramStage, string> = {
  idv: 'IDV & Biometrics',
  kyc: 'KYC',
  aml: 'AML',
  kyb: 'KYB',
  fraud: 'Fraud',
  duplicates: 'Duplicates',
  tm: 'Transaction Monitoring',
}

/** Whether this customer's real Scope record actually has the product switched on — 'unknown'/not-set doesn't count, so we never imply something is configured when it hasn't been confirmed. */
export function isProductEnabled(productKey: InteractionDiagramStage | undefined, scopeData: ScopeData | null): boolean {
  if (!productKey || !scopeData) return true
  switch (productKey) {
    case 'idv': return scopeData.biometricsConfig.enabled === 'yes'
    case 'kyc': return scopeData.kycConfig.kycRequired === 'yes'
    case 'aml': return scopeData.amlConfig.level === 'standard' || scopeData.amlConfig.level === 'enhanced'
    case 'kyb': return scopeData.kybConfig.enabled === 'yes'
    case 'fraud': return scopeData.rulesConfig.fraudEnabled === 'yes'
    case 'duplicates': return scopeData.rulesConfig.duplicateRules === 'default' || scopeData.rulesConfig.duplicateRules === 'custom'
    case 'tm': return scopeData.rulesConfig.transactionMonitoringEnabled === 'yes'
    default: return true
  }
}

export interface CascadeSummary {
  country: string
  countryCode: string
  nodeCount: number
}

export interface ExistingCustomerSelection {
  deal: Deal
  scopeData: ScopeData | null
  cascades: CascadeSummary[]
}

// Extracted so a deal's scope/cascade data can be loaded the exact same way
// from somewhere other than this panel's own search UI — specifically,
// PlatformJourneyTool/index.tsx auto-resolving "this deal's own Scope
// record" from a `dealId` in the URL, rather than requiring the rep to
// re-search for a deal they've already navigated in from.
export async function fetchExistingCustomerSelection(
  deal: Deal,
): Promise<{ selection: ExistingCustomerSelection; error: string | null }> {
  try {
    const [scopeRes, cascadeRes] = await Promise.all([
      scopesApi.getByDealId(deal.id),
      cascadeApi.getProjects(deal.id),
    ])
    let scopeData: ScopeData | null = null
    let cascades: CascadeSummary[] = []
    let error: string | null = null
    if (scopeRes.success && scopeRes.data) {
      scopeData = mapDbScopeToStoreState(scopeRes.data)
    } else {
      error = 'No scope has been recorded for this deal yet.'
    }
    if (cascadeRes.success && cascadeRes.data) {
      cascades = cascadeRes.data.flatMap((p) =>
        p.cascades.map((c) => ({ country: c.country, countryCode: c.countryCode, nodeCount: c.nodes.length }))
      )
    }
    return { selection: { deal, scopeData, cascades }, error }
  } catch {
    return { selection: { deal, scopeData: null, cascades: [] }, error: 'Failed to load this customer’s configuration.' }
  }
}

interface ExistingCustomerPanelProps {
  /** Restrict the grouped output to these Scope sections (e.g. ['AML']). Omit to show everything. */
  sections?: string[]
  title?: string
  description?: string
  /** Show the saved Cascade Builder summary banner. Defaults to true; only meaningful when sections is omitted or includes cascade-relevant sections. */
  showCascadeSummary?: boolean
  /** When set, also render this product's interaction diagram — automatically picking the V1 or V2 variant based on the selected customer's own scoped platform version. */
  productKey?: InteractionDiagramStage
  /** The customer currently selected across the whole Platform Journey tool (lifted to the top level so every stage stays in sync) — null while nothing has been picked yet. */
  sharedSelection: ExistingCustomerSelection | null
  /** Called whenever a customer is picked or cleared here, so the top level can propagate the change to every other stage. Not needed when `searchable` is false. */
  onSelectionChange?: (selection: ExistingCustomerSelection | null) => void
  /** Whether this instance can search for and change the selected customer. Only Account Setup needs this — every other stage just reads whatever was picked there, so they pass `false` to render a read-only view (or a prompt to go pick one) instead of another search box. Defaults to true. */
  searchable?: boolean
}

export function ExistingCustomerPanel({ sections, title, description, showCascadeSummary = true, productKey, sharedSelection, onSelectionChange, searchable = true }: ExistingCustomerPanelProps) {
  const [query, setQuery] = useState('')
  const [deals, setDeals] = useState<Deal[]>([])
  const [searching, setSearching] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(sharedSelection?.deal ?? null)
  const [scopeData, setScopeData] = useState<ScopeData | null>(sharedSelection?.scopeData ?? null)
  const [cascades, setCascades] = useState<CascadeSummary[]>(sharedSelection?.cascades ?? [])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ General: true })

  // Stay in sync with the shared selection — it may change from a different stage (or Reset Journey).
  useEffect(() => {
    setSelectedDeal(sharedSelection?.deal ?? null)
    setScopeData(sharedSelection?.scopeData ?? null)
    setCascades(sharedSelection?.cascades ?? [])
  }, [sharedSelection])

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setDeals([])
      return
    }
    let cancelled = false
    setSearching(true)
    const handle = setTimeout(() => {
      dealsApi.list({ search: q, pageSize: 20 }).then((res) => {
        if (cancelled) return
        setDeals(res.success && res.data ? res.data.items ?? [] : [])
        setSearching(false)
      }).catch(() => { if (!cancelled) setSearching(false) })
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [query])

  async function selectDeal(deal: Deal) {
    setSelectedDeal(deal)
    setScopeData(null)
    setCascades([])
    setError(null)
    setLoading(true)
    const { selection, error } = await fetchExistingCustomerSelection(deal)
    setScopeData(selection.scopeData)
    setCascades(selection.cascades)
    setError(error)
    setLoading(false)
    onSelectionChange?.(selection)
  }

  function changeCustomer() {
    setSelectedDeal(null)
    setScopeData(null)
    setCascades([])
    setQuery('')
    onSelectionChange?.(null)
  }

  const grouped = useMemo(() => {
    if (!scopeData) return []
    const rows = buildExportRows(scopeData).filter((r) => isMeaningful(r.value) && (!sections || sections.includes(r.section)))
    const map: Record<string, typeof rows> = {}
    for (const r of rows) {
      ;(map[r.section] ||= []).push(r)
    }
    const order = sections ?? SECTION_ORDER
    const ordered: [string, typeof rows][] = []
    for (const s of order) {
      if (map[s]?.length) ordered.push([s, map[s]])
    }
    for (const s of Object.keys(map)) {
      if (map[s].length && !ordered.some(([k]) => k === s)) ordered.push([s, map[s]])
    }
    return ordered
  }, [scopeData, sections])

  function toggleSection(section: string) {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const platformVersion = scopeData?.generalInfo.platformVersion
  const productEnabled = isProductEnabled(productKey, scopeData)
  const diagramDsl = productKey && productEnabled ? getInteractionDiagram(productKey, platformVersion) : null

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <div className="px-4 py-3" style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
        <p className="text-[12px] font-semibold text-[#1e293b]">{title ?? 'Existing Customer Configuration'}</p>
        <p className="text-[10px] text-[#64748b] mt-1 leading-snug">
          {description ?? 'Looks up a real customer’s presales record in DealHub (Scope + any saved Cascade Builder design) and shows everything that was configured for them — e.g. Dynamoney. This reads DealHub’s own records, not FrankieOne’s live platform (Operations Center access isn’t wired up here).'}
        </p>
      </div>

      <div className="p-4 space-y-3">
        {searchable && (
          <div className="relative">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
            <input
              className={inputCls}
              style={{ paddingLeft: '28px' }}
              placeholder="Search customer / deal name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {searching && <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] animate-spin" />}
          </div>
        )}

        {searchable && deals.length > 0 && !selectedDeal && (
          <div className="rounded-[9px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
            {deals.map((d) => (
              <button
                key={d.id}
                onClick={() => void selectDeal(d)}
                className="w-full flex items-center justify-between px-3 py-2 text-left transition-colors hover:bg-[#ffffff]"
                style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}
              >
                <span className="text-[11px] font-medium text-[#1e293b]">{d.customer_name}</span>
                <span className="text-[9px] uppercase tracking-wide text-[#64748b]">{d.status.replace('_', ' ')}</span>
              </button>
            ))}
          </div>
        )}

        {searchable && query.trim().length >= 2 && !searching && deals.length === 0 && !selectedDeal && (
          <p className="text-[10px] text-[#475569]">No deals found matching &quot;{query.trim()}&quot;.</p>
        )}

        {!searchable && !selectedDeal && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px dashed #e5e7eb' }}>
            <FileSearch size={12} className="text-[#64748b] shrink-0" />
            <p className="text-[11px] text-[#64748b]">Select a customer on Account Setup to see their {title ? title.split('—')[0].trim() : 'configuration'} here.</p>
          </div>
        )}

        {selectedDeal && (
          <div className="space-y-3">
            <div className="flex items-center justify-between px-3 py-2 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="flex items-center gap-2">
                <FileSearch size={12} className="text-[#4f46e5]" />
                <span className="text-[11px] font-semibold text-[#1e293b]">{selectedDeal.customer_name}</span>
                {!loading && scopeData && (
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                    style={platformVersion === 'v1'
                      ? { background: 'rgba(251,146,60,.15)', color: '#c2410c' }
                      : platformVersion === 'v2'
                      ? { background: 'rgba(52,211,153,.15)', color: '#15803d' }
                      : { background: 'rgba(148,163,184,.15)', color: '#475569' }
                    }
                  >
                    {platformVersion === 'v1' ? 'V1' : platformVersion === 'v2' ? 'V2' : 'Version unknown'}
                  </span>
                )}
              </div>
              {searchable && (
                <button
                  onClick={changeCustomer}
                  className="text-[10px] text-[#64748b] hover:text-[#1e293b]"
                >
                  Change customer
                </button>
              )}
            </div>

            {loading && (
              <div className="flex items-center gap-2 py-6 justify-center text-[#64748b]">
                <Loader2 size={14} className="animate-spin" />
                <span className="text-[11px]">Loading configuration…</span>
              </div>
            )}

            {error && !loading && (
              <p className="text-[10px] text-amber-700 flex items-center gap-1.5"><AlertCircle size={11} /> {error}</p>
            )}

            {!loading && scopeData && productKey && !productEnabled && (
              <div className="px-3 py-2.5 rounded-[9px] flex items-start gap-2" style={{ background: 'rgba(251,146,60,.06)', border: '1px solid rgba(251,146,60,.25)' }}>
                <AlertCircle size={13} className="text-amber-700 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-700">
                  {selectedDeal?.customer_name ?? 'This customer'} doesn&apos;t have {PRODUCT_LABELS[productKey]} configured in DealHub&apos;s scope record.
                </p>
              </div>
            )}

            {!loading && diagramDsl && scopeData && (
              <div className="space-y-1.5">
                {platformVersion !== 'v1' && platformVersion !== 'v2' && (
                  <p className="text-[10px] text-[#475569] flex items-center gap-1.5">
                    <AlertCircle size={11} className="shrink-0" /> This customer&apos;s platform version isn&apos;t recorded in DealHub — showing the V2 flow by default.
                  </p>
                )}
                <InteractionDiagram stageId={`${productKey}-existing`} dsl={diagramDsl} />
              </div>
            )}

            {!loading && showCascadeSummary && cascades.length > 0 && (
              <div className="px-3 py-2 rounded-[9px] flex items-center gap-2" style={{ background: 'rgba(129,140,248,.08)', border: '1px solid rgba(129,140,248,.25)' }}>
                <GitBranch size={12} className="text-[#4f46e5]" />
                <span className="text-[10px] text-[#4f46e5]">
                  {cascades.length} saved cascade design{cascades.length === 1 ? '' : 's'}: {cascades.map((c) => `${c.country} (${c.nodeCount} nodes)`).join(', ')}
                </span>
              </div>
            )}

            {!loading && scopeData && productEnabled && grouped.length === 0 && (
              <p className="text-[10px] text-[#475569]">This customer has a scope record, but no fields have been configured yet.</p>
            )}

            {!loading && productEnabled && grouped.map(([section, rows]) => (
              <div key={section} className="rounded-[9px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
                <button
                  onClick={() => toggleSection(section)}
                  className="w-full flex items-center justify-between px-3 py-2 transition-colors hover:bg-[#ffffff]"
                  style={{ background: '#f8fafc' }}
                >
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[#475569]">{section}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[9px] text-[#475569]">{rows.length}</span>
                    {openSections[section] ? <ChevronUp size={11} className="text-[#64748b]" /> : <ChevronDown size={11} className="text-[#64748b]" />}
                  </span>
                </button>
                {openSections[section] && (
                  <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
                    {rows.map((r, i) => (
                      <div key={`${r.field}-${i}`} className="flex items-start gap-3 px-3 py-2" style={{ borderTop: i === 0 ? 'none' : '1px solid #e5e7eb', background: '#f8fafc' }}>
                        <span className="text-[10px] text-[#64748b] w-2/5 shrink-0">{r.displayField || r.field}</span>
                        <span className="text-[10px] text-[#1e293b] break-words">{r.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
